import { Injectable } from '@angular/core';
import mermaid from 'mermaid';

/**
 * Service to initialize and render Mermaid diagrams in the DOM.
 * Mermaid diagrams are identified by the class 'mermaid-diagram'.
 */
@Injectable({
  providedIn: 'root',
})
export class MermaidService {
  private initialized = false;

  constructor() {
    this.initializeMermaid();
  }

  /**
   * Initialize mermaid with configuration
   */
  private initializeMermaid(): void {
    if (this.initialized) return;

    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        logLevel: 'error', // Suppress verbose logging
      });
      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize Mermaid:', error);
    }
  }

  /**
   * Render all mermaid diagrams in a given container element.
   * This should be called after content is rendered in the DOM.
   *
   * @param container - The DOM element containing mermaid diagrams
   */
  async renderDiagrams(container: HTMLElement | null): Promise<void> {
    if (!container) return;

    try {
      // Only select diagrams not yet rendered (still have the data-mermaid-src attribute).
      const diagramContainers = container.querySelectorAll('.mermaid-diagram[data-mermaid-src]');

      if (diagramContainers.length === 0) return;

      // Convert NodeList to Array for proper iteration
      const diagrams = Array.from(diagramContainers) as HTMLElement[];

      for (const diagram of diagrams) {
        // Read the mermaid source from the data attribute set by the markdown pipe.
        // Using encodeURIComponent/decodeURIComponent keeps all special characters safe.
        const mermaidCode = decodeURIComponent(diagram.getAttribute('data-mermaid-src') || '').trim();

        if (!mermaidCode) continue;

        // Remove the attribute immediately so that concurrent ngAfterViewChecked
        // calls don't pick this element up again while mermaid.render() is awaited.
        diagram.removeAttribute('data-mermaid-src');

        try {
          const uniqueId = `mermaid-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
          const { svg } = await mermaid.render(uniqueId, this.normalizeDiagramType(mermaidCode));
          diagram.innerHTML = svg;
        } catch (error) {
          console.warn('Failed to render Mermaid diagram:', error);
          diagram.innerHTML = '<span class="mermaid-error">Failed to render diagram. Invalid syntax?</span>';
        }
      }
    } catch (error) {
      console.error('Error in renderDiagrams:', error);
    }
  }

  /**
   * Apply the appropriate mermaid theme based on the current document theme
   */
  private applyTheme(): void {
    try {
      const isDarkMode = document.documentElement.classList.contains('dark');
      const theme = isDarkMode ? 'dark' : 'default';

      mermaid.initialize({ theme });
    } catch (error) {
      console.warn('Failed to apply mermaid theme:', error);
    }
  }

  /**
   * LLMs sometimes generate incorrect or slightly malformed mermaid syntax.
   * This normalizes common issues to their correct mermaid equivalents.
   */
  private normalizeDiagramType(code: string): string {
    const lines = code.split('\n');
    const firstLine = lines[0].trim().toLowerCase();

    // "bar" with xychart axes => xychart-beta
    if (firstLine === 'bar' && /x-axis|y-axis/i.test(code)) {
      lines[0] = 'xychart-beta';
      return this.normalizeXychartLines(lines).join('\n');
    }

    // "barchart" or "bar chart" => xychart-beta
    if (/^bar[\s-]?chart$/i.test(firstLine)) {
      lines[0] = 'xychart-beta';
      return this.normalizeXychartLines(lines).join('\n');
    }

    // Already xychart-beta — still fix potential title quoting issue
    if (firstLine === 'xychart-beta') {
      return this.normalizeXychartLines(lines).join('\n');
    }

    return code;
  }

  /**
   * xychart-beta requires the title value to be quoted.
   * LLMs often emit: `title My Chart (units)` — fix to: `title "My Chart (units)"`
   */
  private normalizeXychartLines(lines: string[]): string[] {
    return lines.map((line) => {
      const m = line.match(/^(\s*title\s+)([^"].+)$/i);
      if (m) {
        return `${m[1]}"${m[2].trim()}"`;
      }
      return line;
    });
  }
}
